import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

/**
 * The promotional chips a product carries — "Novinka", "Akce", a seasonal
 * sticker — as a stack and as one chip.
 *
 *   <ProductLabelList>
 *     {labels.map((label) => (
 *       <ProductLabelMark
 *         key={label.code}
 *         variant={ProductLabelVariant.ROOMY}
 *         style={{ color: label.textColor, backgroundColor: label.color }}
 *       >
 *         <Icon /> {label.name}
 *       </ProductLabelMark>
 *     ))}
 *   </ProductLabelList>
 *
 * Its own module rather than a part of either section, because both the product
 * card and the product gallery hang the same chips in their own corner box: the
 * card's `ProductCardLabels` and the gallery's `ProductGalleryLabels` are the
 * positioned boxes, and this is what goes in them. A chip duplicated per
 * section would drift between the two the first time one of them is restyled.
 *
 * The colours are CMS data and ride the spread native `style` prop as final CSS
 * values computed app-side — this package never names a CMS variable. Which
 * labels exist, whether any are filtered out, and their copy are the tenant's.
 */

/**
 * How much room a chip takes. An appearance axis and not a size one only in the
 * sense that matters here: the two callers sit at different scales — a chip over
 * a 726px-tall product photo and a chip over a 187px card thumbnail — and the
 * padding is what makes each read as the same object at its own scale.
 */
export const productLabelMarkVariants = cva(
  'tracking-extra-wide flex w-max items-center gap-2 text-xs uppercase',
  {
    variants: {
      variant: {
        roomy: 'px-4 py-2',
        compact: 'px-3 py-1',
        unstyled: '',
      },
    },
  }
);

type ProductLabelVariantName = NonNullable<
  VariantProps<typeof productLabelMarkVariants>['variant']
>;

export type ProductLabelVariant = ProductLabelVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductLabelVariant = {
  ROOMY: 'roomy',
  COMPACT: 'compact',
  /**
   * The opt-out: a tenant painting the chip itself. Carried as an empty entry
   * rather than left out of the recipe, so what it paints — nothing — is stated
   * in the list instead of decided by absence from it.
   */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductLabelVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductLabelVariantProps = { readonly variant: ProductLabelVariant };

/** The column the chips stack in. No `variant`: they stack the same way at both scales. */
const productLabelListClasses = 'flex flex-col gap-2';

export type ProductLabelListProps = ComponentProps<'div'>;

/** The chip stack. */
export const ProductLabelList = ({ className, ...props }: ProductLabelListProps) => (
  <div
    data-slot="product-label-list"
    className={clsxm(productLabelListClasses, className)}
    {...props}
  />
);

export type ProductLabelMarkProps = ComponentProps<'div'> & ProductLabelVariantProps;

/**
 * One chip. Renders a neutral element and carries no colour of its own: a
 * label's fill and text colour are per-instance CMS values, so they arrive
 * through `style` rather than through a token or a prop this package names.
 */
export const ProductLabelMark = ({ className, variant, ...props }: ProductLabelMarkProps) => (
  <div
    data-slot="product-label-mark"
    className={clsxm(productLabelMarkVariants({ variant }), className)}
    {...props}
  />
);
